import { useState, useCallback, useEffect, useRef } from 'react';
import type { Socket } from 'socket.io-client';
import type { Annotation } from '../types';
import type { SignalingService } from '@/features/videocall/services/signaling.service';

interface UseAnnotationsProps {
  roomId: string;
  socketRef: React.MutableRefObject<Socket | null>;
  signalingServiceRef: React.MutableRefObject<SignalingService | null>;
  activePanel: 'chat' | 'notes' | 'annotate' | 'measure' | null;
}

export function useAnnotations({
  roomId,
  socketRef,
  signalingServiceRef,
  activePanel,
}: UseAnnotationsProps) {
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [selectedAnnotationTool, setSelectedAnnotationTool] = useState<'pencil' | 'square' | 'circle' | 'arrow' | null>('pencil');
  const [selectedColor, setSelectedColor] = useState('#ff0000');
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentDrawing, setCurrentDrawing] = useState<{x: number, y: number} | null>(null);
  const [dragState, setDragState] = useState<{
    annotationId: string;
    startX: number;
    startY: number;
    initialX: number;
    initialY: number;
    initialEndX?: number;
    initialEndY?: number;
    initialPath?: Array<{x: number, y: number}>;
  } | null>(null);

  // Request animation frame for smooth drawing
  const rafIdRef = useRef<number | null>(null);

  const updateAnnotations = useCallback((
    updater: Annotation[] | ((prev: Annotation[]) => Annotation[]),
    options: { skipBroadcast?: boolean } = {}
  ) => {
    setAnnotations(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;

      // ✅ Use SignalingService instead of direct socket access
      if (!options.skipBroadcast) {
        const signalingService = signalingServiceRef.current;
        if (signalingService) {
          console.log('📡 Broadcasting annotations via SignalingService:', next.length);
          signalingService.sendAnnotationSync(next);
        } else {
          console.warn('⚠️ SignalingService not available for annotation broadcast');
        }
      }

      return next;
    });
  }, [signalingServiceRef]);

  const handleShapeDragStart = (e: React.MouseEvent<HTMLDivElement | SVGSVGElement | SVGElement>, annotationId: string) => {
    e.stopPropagation();
    e.preventDefault();
    
    const videoContainer = document.querySelector('[data-annotation-container]') as HTMLElement;
    if (!videoContainer) return;

    const rect = videoContainer.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const annotation = annotations.find(a => a.id === annotationId);
    if (!annotation) return;

    const dragStateData: any = {
      annotationId,
      startX: x,
      startY: y,
      initialX: annotation.x,
      initialY: annotation.y,
      initialEndX: annotation.endX,
      initialEndY: annotation.endY
    };

    if (annotation.type === 'pencil' && annotation.path) {
      dragStateData.initialPath = [...annotation.path];
    }

    setDragState(dragStateData);
  };

  const handleShapeDragMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragState) return;

    const videoContainer = document.querySelector('[data-annotation-container]') as HTMLElement;
    if (!videoContainer) return;

    const rect = videoContainer.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    const deltaX = x - dragState.startX;
    const deltaY = y - dragState.startY;
    
    updateAnnotations(prev => prev.map(annotation => {
      if (annotation.id === dragState.annotationId) {
        const updatedAnnotation = {
          ...annotation,
          x: Math.max(0, Math.min(100, dragState.initialX + deltaX)),
          y: Math.max(0, Math.min(100, dragState.initialY + deltaY)),
          endX: dragState.initialEndX ? Math.max(0, Math.min(100, dragState.initialEndX + deltaX)) : undefined,
          endY: dragState.initialEndY ? Math.max(0, Math.min(100, dragState.initialEndY + deltaY)) : undefined
        };

        if (annotation.type === 'pencil' && dragState.initialPath) {
          updatedAnnotation.path = dragState.initialPath.map(point => ({
            x: Math.max(0, Math.min(100, point.x + deltaX)),
            y: Math.max(0, Math.min(100, point.y + deltaY))
          }));
        }

        return updatedAnnotation;
      }
      return annotation;
    }));
  };

  const handleShapeDragEnd = () => {
    setDragState(null);
  };

  const handleAnnotationMouseDown = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!isAnnotating || !selectedAnnotationTool || dragState) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;

    console.log('🖊️ Annotation MouseDown:', { tool: selectedAnnotationTool, x, y });

    setIsDrawing(true);
    setCurrentDrawing({ x, y });

    if (selectedAnnotationTool === 'pencil') {
      const newAnnotation: Annotation = {
        id: Date.now().toString(),
        type: 'pencil',
        color: selectedColor,
        x,
        y,
        path: [{ x, y }]
      };
      console.log('✏️ Created pencil annotation:', newAnnotation);
      updateAnnotations(prev => [...prev, newAnnotation]);
    }
  };

  const handleAnnotationMouseMove = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (dragState) {
      handleShapeDragMove(e as React.MouseEvent<HTMLDivElement>);
      return;
    }

    if (!isDrawing || !currentDrawing || !isAnnotating || !selectedAnnotationTool) return;

    // ⚠️ CRITICAL: Capture rect and coordinates BEFORE RAF to avoid null currentTarget
    if (!e.currentTarget) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;

    // Cancel any pending animation frame
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }

    // Use requestAnimationFrame for smooth rendering
    rafIdRef.current = requestAnimationFrame(() => {

      if (selectedAnnotationTool === 'pencil') {
        // Local state update only during drawing - no broadcast
        // Throttle points: only add if distance from last point > threshold
        setAnnotations(prev => {
          const lastAnnotation = prev[prev.length - 1];
          if (lastAnnotation && lastAnnotation.type === 'pencil' && lastAnnotation.path) {
            const lastPoint = lastAnnotation.path[lastAnnotation.path.length - 1];
            const distance = Math.sqrt(
              Math.pow(x - lastPoint.x, 2) + Math.pow(y - lastPoint.y, 2)
            );

            // Lower threshold to 0.1% for more responsive drawing
            // Always add first point (when path.length === 1) to ensure line is visible
            if (distance > 0.1 || lastAnnotation.path.length === 1) {
              const updatedAnnotation = {
                ...lastAnnotation,
                path: [...lastAnnotation.path, { x, y }]
              };
              console.log('✏️ Added point to pencil, path length:', updatedAnnotation.path.length);
              return [...prev.slice(0, -1), updatedAnnotation];
            }
          }
          return prev;
        });
      } else if (selectedAnnotationTool === 'arrow') {
        setAnnotations(prev => {
          const filtered = prev.filter(a => a.id !== 'temp');
          const tempAnnotation: Annotation = {
            id: 'temp',
            type: selectedAnnotationTool,
            color: selectedColor,
            x: currentDrawing.x,
            y: currentDrawing.y,
            endX: x,
            endY: y
          };
          console.log('🏹 Created temp arrow:', tempAnnotation);
          return [...filtered, tempAnnotation];
        });
      } else {
        const width = Math.abs(x - currentDrawing.x);
        const height = Math.abs(y - currentDrawing.y);

        // Calculate top-left corner based on drag direction
        const left = Math.min(x, currentDrawing.x);
        const top = Math.min(y, currentDrawing.y);

        setAnnotations(prev => {
          const filtered = prev.filter(a => a.id !== 'temp');
          const tempAnnotation: Annotation = {
            id: 'temp',
            type: selectedAnnotationTool,
            color: selectedColor,
            x: left,
            y: top,
            width,
            height,
            endX: x,
            endY: y
          };
          console.log(`📐 Created temp ${selectedAnnotationTool}:`, { left, top, width, height, tempAnnotation });
          return [...filtered, tempAnnotation];
        });
      }
    });
  };

  const handleAnnotationMouseUp = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (dragState) {
      handleShapeDragEnd();
      return;
    }

    if (!isDrawing || !currentDrawing) return;

    // Broadcast the final annotation to peer
    if (selectedAnnotationTool === 'pencil') {
      // For pencil, broadcast the completed drawing
      updateAnnotations(prev => prev); // This will trigger broadcast of current state
    } else {
      // ⚠️ CRITICAL: Check if currentTarget exists (null on mouseLeave)
      if (!e.currentTarget) {
        // Mouse left the drawing area - finalize with last known temp annotation
        console.log('⚠️ Mouse left drawing area, finalizing temp annotation');
        updateAnnotations(prev => {
          const tempAnnotation = prev.find(a => a.id === 'temp');
          if (!tempAnnotation) return prev.filter(a => a.id !== 'temp');

          // Convert temp to final annotation
          const finalAnnotation: Annotation = {
            ...tempAnnotation,
            id: Date.now().toString(),
          };
          return [...prev.filter(a => a.id !== 'temp'), finalAnnotation];
        });
      } else {
        const rect = e.currentTarget.getBoundingClientRect();
        const clientX = 'changedTouches' in e ? e.changedTouches[0].clientX : e.clientX;
        const clientY = 'changedTouches' in e ? e.changedTouches[0].clientY : e.clientY;
        const x = ((clientX - rect.left) / rect.width) * 100;
        const y = ((clientY - rect.top) / rect.height) * 100;

      if (selectedAnnotationTool === 'arrow') {
        updateAnnotations(prev => {
          const filtered = prev.filter(a => a.id !== 'temp');
          const finalAnnotation: Annotation = {
            id: Date.now().toString(),
            type: selectedAnnotationTool,
            color: selectedColor,
            x: currentDrawing.x,
            y: currentDrawing.y,
            endX: x,
            endY: y
          };
          return [...filtered, finalAnnotation];
        });
      } else {
        const width = Math.abs(x - currentDrawing.x);
        const height = Math.abs(y - currentDrawing.y);

        // Calculate top-left corner based on drag direction
        const left = Math.min(x, currentDrawing.x);
        const top = Math.min(y, currentDrawing.y);

        updateAnnotations(prev => {
          const filtered = prev.filter(a => a.id !== 'temp');
          const finalAnnotation: Annotation = {
            id: Date.now().toString(),
            type: selectedAnnotationTool,
            color: selectedColor,
            x: left,
            y: top,
            width: Math.max(width, 2),
            height: Math.max(height, 2),
            endX: x,
            endY: y
          };
          return [...filtered, finalAnnotation];
        });
      }
      }
    }

    setIsDrawing(false);
    setCurrentDrawing(null);
  };

  const clearAnnotations = () => {
    updateAnnotations([]);
    setIsDrawing(false);
    setCurrentDrawing(null);
  };

  // ✅ FIXED: Listen for incoming annotations from peer via SignalingService
  useEffect(() => {
    const signalingService = signalingServiceRef.current;
    if (!signalingService) {
      console.log('⏳ SignalingService not ready, waiting...');
      return;
    }

    console.log('🎧 Setting up annotation sync listener via SignalingService');

    const cleanup = signalingService.onAnnotationSync((payload) => {
      console.log('📝 Received annotations from peer via SignalingService:', payload.annotations.length);

      // Update annotations without broadcasting back
      setAnnotations(payload.annotations);
    });

    return cleanup;
  }, [signalingServiceRef]);

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  return {
    annotations,
    selectedAnnotationTool,
    selectedColor,
    isAnnotating,
    setSelectedAnnotationTool,
    setSelectedColor,
    setIsAnnotating,
    handleAnnotationMouseDown,
    handleAnnotationMouseMove,
    handleAnnotationMouseUp,
    clearAnnotations,
  };
}